import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { X } from 'lucide-react'
import { superadminApi, type UserSearchResultDto } from '@/api/superadmin'

export const ROLE_LABEL: Record<string, string> = {
  TenantAdmin: 'Admin',
  Trainer: 'Trainer',
  ClientManager: 'Client manager',
  StaffMember: 'Client staff',
  SuperAdmin: 'Super admin',
}

function errorMessage(e: unknown) {
  const data = (e as { response?: { data?: { detail?: string; title?: string } } })?.response?.data
  return data?.detail ?? data?.title ?? 'Something went wrong. Please try again.'
}

function Shell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-4">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600" aria-label="Close"><X size={18} /></button>
        <h2 className="text-base font-semibold text-gray-900 pr-6">{title}</h2>
        {children}
      </div>
    </div>
  )
}

const primary = 'px-4 py-2 text-sm font-medium rounded-lg text-white disabled:opacity-50 cursor-pointer'

export function ChangeEmailDialog({ account, onClose, onDone }: { account: UserSearchResultDto; onClose: () => void; onDone: () => void }) {
  const [email, setEmail] = useState('')
  const mutation = useMutation({
    mutationFn: () => superadminApi.changeUserEmail(account.id, email.trim()),
    onSuccess: () => { onDone(); onClose() },
  })

  return (
    <Shell title="Change email address" onClose={onClose}>
      <p className="text-sm text-gray-600">
        Current address: <span className="font-medium text-gray-900">{account.email}</span>
      </p>
      <input
        type="email"
        autoFocus
        value={email}
        onChange={e => setEmail(e.target.value)}
        placeholder="New email address"
        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />
      <ul className="text-xs text-gray-500 list-disc pl-4 space-y-0.5">
        <li>The old address is freed, so it can be used for another account straight away.</li>
        {account.linkedTo && <li>Their record ({account.linkedTo.replace(/^[A-Za-z ]+: /, '')}) is updated to match.</li>}
        <li>Anything signed in as this account is signed out.</li>
      </ul>
      {mutation.isError && <p className="text-sm text-red-600">{errorMessage(mutation.error)}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">Cancel</button>
        <button type="button" onClick={() => mutation.mutate()} disabled={!email.includes('@') || mutation.isPending} className={`${primary} bg-indigo-600 hover:bg-indigo-700`}>
          {mutation.isPending ? 'Saving…' : 'Change email'}
        </button>
      </div>
    </Shell>
  )
}

export function ChangeRoleDialog({ account, onClose, onDone }: { account: UserSearchResultDto; onClose: () => void; onDone: () => void }) {
  const options = account.actions.roleOptions
  const [role, setRole] = useState(options[0] ?? '')
  const mutation = useMutation({
    mutationFn: () => superadminApi.changeUserRole(account.id, role),
    onSuccess: () => { onDone(); onClose() },
  })

  return (
    <Shell title="Change role" onClose={onClose}>
      <p className="text-sm text-gray-600">
        <span className="font-medium text-gray-900">{account.email}</span> is currently <span className="font-medium">{ROLE_LABEL[account.role] ?? account.role}</span>.
      </p>
      <select
        value={role}
        onChange={e => setRole(e.target.value)}
        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
      >
        {options.map(o => <option key={o} value={o}>{ROLE_LABEL[o] ?? o}</option>)}
      </select>
      <p className="text-xs text-gray-500">They'll be signed out and will see the new role's screens next time they sign in.</p>
      {mutation.isError && <p className="text-sm text-red-600">{errorMessage(mutation.error)}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">Cancel</button>
        <button type="button" onClick={() => mutation.mutate()} disabled={!role || mutation.isPending} className={`${primary} bg-indigo-600 hover:bg-indigo-700`}>
          {mutation.isPending ? 'Saving…' : 'Change role'}
        </button>
      </div>
    </Shell>
  )
}

/** A confirmation that says exactly what will happen, for the actions that are hard to undo. */
export function ConfirmDialog({
  title, children, confirmLabel, danger, run, onClose, onDone,
}: {
  title: string
  children: React.ReactNode
  confirmLabel: string
  danger?: boolean
  run: () => Promise<unknown>
  onClose: () => void
  onDone: () => void
}) {
  const mutation = useMutation({
    mutationFn: run,
    onSuccess: () => { onDone(); onClose() },
  })

  return (
    <Shell title={title} onClose={onClose}>
      <div className="text-sm text-gray-600 space-y-2">{children}</div>
      {mutation.isError && <p className="text-sm text-red-600">{errorMessage(mutation.error)}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 cursor-pointer">Cancel</button>
        <button type="button" onClick={() => mutation.mutate()} disabled={mutation.isPending} className={`${primary} ${danger ? 'bg-red-600 hover:bg-red-700' : 'bg-indigo-600 hover:bg-indigo-700'}`}>
          {mutation.isPending ? 'Working…' : confirmLabel}
        </button>
      </div>
    </Shell>
  )
}
